/**
 * Single entry point for surfacing client-side errors. Replaces ad-hoc
 * console.error() calls scattered across pages so we can:
 *
 *  - silence noise in jest/CI by default,
 *  - swap in Sentry / DataDog RUM with one edit instead of grep,
 *  - keep the eslint rule `no-console` strict (set to "error") without
 *    every page disabling it inline.
 *
 * Use it like console.error: logError("Failed to load taps", err);
 *
 * In production (`NODE_ENV === "production"`) we still write to the console
 * because users sometimes need to copy stacks for support. Wire a real
 * reporter by replacing the body of `report` below.
 */

let reporter = null;

/**
 * Hook a real client-side reporter (Sentry.captureException, etc.). Pass null
 * to clear. Tests use this to assert that a code path emitted a report.
 */
export function registerErrorReporter(fn) {
  reporter = fn;
}

export function logError(message, error, context) {
  // Always send to the registered reporter — it's the production sink.
  if (reporter) {
    try {
      reporter(message, error, context);
    } catch {
      // Reporter must never crash the caller; swallow secondary failures.
    }
  }

  if (process.env.NODE_ENV !== "test") {
    // Keep dev/prod consoles alive — operators and support engineers rely
    // on them. Tests stay quiet by default to keep output clean.
    // eslint-disable-next-line no-console
    console.error(message, error, context ?? "");
  }
}
